import { Fragment, useEffect, useState } from "react";
import { Save, Loader2, RefreshCw, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getFoundationARStatus, getFoundationARLog } from "@/lib/api";
import ARTriggerInspector from "./ARTriggerInspector";
import StagingSchemaPanel from "./StagingSchemaPanel";

const GL_KEYS = ["ar_gl_income", "ar_gl_ar", "ar_gl_tax", "ar_gl_retainage"];
const DIV_KEYS = ["ar_div_level_1", "ar_div_level_2", "ar_div_level_3"];
const TEXT_KEYS = ["ar_income_type_no", "ar_terms_no", "ar_due_days"];

function Row({ label, note, children, testId }) {
  return (
    <div className="grid grid-cols-12 gap-3 items-start py-2 border-b border-slate-100 last:border-b-0" data-testid={testId}>
      <div className="col-span-4">
        <div className="text-xs font-semibold text-slate-700 font-mono">{label}</div>
        <div className="text-[11px] text-slate-500 leading-snug">{note}</div>
      </div>
      <div className="col-span-8 flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}

function GlBadge({ st, testId }) {
  if (!st) return null;
  if (!st.found) return <span className="inline-flex items-center gap-1 text-[11px] text-rose-700 font-medium" data-testid={testId}><XCircle className="w-3 h-3" /> not in synced accounts</span>;
  const bad = st.ok === false;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] ${bad ? "text-rose-700 font-medium" : "text-emerald-700"}`} data-testid={testId} title={st.gate || ""}>
      {bad ? <XCircle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />} {st.description}{st.needs_divisions ? " · takes divisions" : " · no divisions"}{bad ? " — BLOCKS push" : ""}
    </span>
  );
}

function DivBadge({ st, testId }) {
  if (!st) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] ${st.ok ? "text-emerald-700" : "text-rose-700 font-medium"}`} data-testid={testId}>
      {st.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {st.ok ? st.label : st.reason}
    </span>
  );
}

export default function FoundationARSettings({ spec, values, draft, setDraft, save, saving, canWrite }) {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const load = () => Promise.all([getFoundationARStatus(), getFoundationARLog(20)])
    .then(([s, l]) => { setStatus(s.data); setLog(l.data); })
    .catch(() => toast.error("Could not load Foundation AR status"));
  useEffect(() => { load(); }, [values]);

  const c = status?.control_ar;
  const missing = status ? GL_KEYS.filter(k => !status.gl[k]?.found) : [];

  return (
    <section className="rounded-md border border-emerald-200 p-3" data-testid="fs-ar-section">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div>
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">AR invoice push (Phase 3)</div>
          <div className="text-[11px] text-slate-500">Direct write to <code>ar_invoice</code> + <code>ar_invoice_gl</code> as unposted invoices. Accounting posts in Foundation.</div>
        </div>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={load} data-testid="fs-ar-refresh"><RefreshCw className="w-3 h-3 mr-1" />Refresh</Button>
      </div>

      <Row label={spec.ar_push_enabled.label} note={spec.ar_push_enabled.note} testId="fs-row-ar_push_enabled">
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={!!values.ar_push_enabled} disabled={!canWrite || saving === "ar_push_enabled"}
                 onChange={e => save("ar_push_enabled", e.target.checked)} className="h-4 w-4 rounded border-slate-300 accent-emerald-600" data-testid="fs-toggle-ar_push_enabled" />
          <span className={values.ar_push_enabled ? "text-emerald-700 font-semibold" : "text-slate-600"}>{values.ar_push_enabled ? "ON — pushes allowed" : "OFF — every AR push is refused"}</span>
        </label>
      </Row>

      {status && (
        <div className="my-2 rounded-md bg-slate-50 border border-slate-200 p-2 text-[11px] text-slate-700 space-y-0.5" data-testid="fs-ar-control">
          <div><strong>Bridge</strong> {status.bridge.ok ? `v${status.bridge.version}` : "offline"} · {status.bridge.capable ? <span className="text-emerald-700">insert-ar-invoice supported</span> : <span className="text-amber-800">no insert-ar-invoice endpoint (deploy v2.3)</span>}</div>
          {c ? (
            <div><strong>control_ar</strong> enable_cc_dist={c.enable_cc_dist} {c.enable_cc_dist === "Y" && <span className="text-rose-700 font-semibold">— J/C distribution enabled, Foundation rejects imports</span>} · hidden_tax_type={c.hidden_tax_type} · control_date_flag={c.control_date_flag}{c.control_date_flag === "P" && ` · period ${String(c.start_date).slice(0, 10)} → ${String(c.end_date).slice(0, 10)}`}</div>
          ) : <div className="text-rose-700">control_ar unreadable: {status.control_ar_error}</div>}
          {!status.accounts_synced && <div className="text-amber-800">Accounts list not synced — cannot validate GL accounts. Every AR push is blocked until Reference sync → accounts runs.</div>}
          {missing.length > 0 && <div className="flex items-center gap-1 text-rose-700 font-medium" data-testid="fs-ar-gl-warning"><AlertTriangle className="w-3 h-3" />{missing.length} configured GL account{missing.length > 1 ? "s" : ""} would block the push — see the red badges below.</div>}
        </div>
      )}

      {[...GL_KEYS, ...TEXT_KEYS, "ar_hidden_sales_tax"].map(k => (
        <Fragment key={k}>
        {k === "ar_gl_ar" && (
          <div className="mt-2 mb-1 rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="fs-ar-service-divisions">
            <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">Service divisions ({values.ar_div_level_1} / {values.ar_div_level_2} / {values.ar_div_level_3})</div>
            <div className="text-[11px] text-slate-500 mb-1">Applied to <code>gl_income</code> only. gl_ar / gl_tax / gl_retainage are balance-sheet accounts — sent bare, division columns never attached.</div>
            {DIV_KEYS.map((dk, i) => (
              <Row key={dk} label={spec[dk].label} note={spec[dk].note} testId={`fs-row-${dk}`}>
                <Input value={draft[dk] ?? ""} onChange={e => setDraft(d => ({ ...d, [dk]: e.target.value }))} disabled={!canWrite}
                       className="h-8 w-20 font-mono text-xs" maxLength={4} data-testid={`fs-input-${dk}`} />
                <Button size="sm" variant="outline" className="h-8" disabled={!canWrite || saving === dk || String(draft[dk] ?? "") === String(values[dk] ?? "")}
                        onClick={() => save(dk, draft[dk])} data-testid={`fs-save-${dk}`}>
                  {saving === dk ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                </Button>
                <DivBadge st={status?.divisions?.[i]} testId={`fs-ar-div-badge-${dk}`} />
              </Row>
            ))}
          </div>
        )}
        <Row label={spec[k].label} note={spec[k].note} testId={`fs-row-${k}`}>
          {k === "ar_hidden_sales_tax" ? (
            <div className="flex rounded-md border border-slate-200 overflow-hidden">
              {["N", "Y"].map(m => (
                <button key={m} type="button" disabled={!canWrite} onClick={() => save(k, m)}
                        className={`px-2.5 h-8 text-xs border-r last:border-r-0 border-slate-200 ${values[k] === m ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                        data-testid={`fs-ar-hidden-${m}`}>{m}</button>
              ))}
            </div>
          ) : (
            <>
              <Input value={draft[k] ?? ""} onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))} disabled={!canWrite}
                     className="h-8 w-36 font-mono text-xs" maxLength={spec[k].max || 12} data-testid={`fs-input-${k}`} />
              <Button size="sm" variant="outline" className="h-8" disabled={!canWrite || saving === k || String(draft[k] ?? "") === String(values[k] ?? "")}
                      onClick={() => save(k, draft[k])} data-testid={`fs-save-${k}`}>
                {saving === k ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              </Button>
              {GL_KEYS.includes(k) && <GlBadge st={status?.gl?.[k]} testId={`fs-ar-gl-badge-${k}`} />}
              {k === "ar_income_type_no" && status?.income_type && (
                <span className={`text-[11px] ${status.income_type.found ? "text-emerald-700" : "text-rose-700"}`}>{status.income_type.found ? "exists in income_types" : "not in income_types"}</span>
              )}
              {k === "ar_terms_no" && status?.terms && (
                <span className={`text-[11px] ${status.terms.found ? "text-emerald-700" : "text-rose-700"}`}>{status.terms.found ? "exists in terms" : "not in terms"}</span>
              )}
            </>
          )}
        </Row>
        </Fragment>
      ))}

      <ARTriggerInspector />
      <StagingSchemaPanel />

      <div className="mt-3">
        <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide mb-1">AR push attempts</div>
        <div className="rounded-md border border-slate-200 max-h-48 overflow-y-auto text-[11px]" data-testid="fs-ar-log">
          {log.length === 0 && <div className="p-2 text-slate-400">No AR push attempts yet.</div>}
          {log.map(r => (
            <div key={r.id} className="px-2 py-1 border-b border-slate-100 last:border-b-0" data-testid={`fs-ar-log-row-${r.id}`}>
              <div className="flex gap-2">
                <span className="text-slate-400 w-32 shrink-0">{String(r.created_at).replace("T", " ").slice(0, 16)}</span>
                <span className="font-mono w-24 shrink-0">{(r.invoice_no_sent || "").trim()}</span>
                <span className={`w-20 shrink-0 font-semibold ${r.outcome === "success" ? "text-emerald-700" : "text-rose-700"}`}>{r.outcome}</span>
                <span className="text-slate-500 w-36 shrink-0 truncate">{r.target_database || r.required_database}</span>
                <span className="text-slate-600 truncate" title={r.message}>{r.plain_error || r.message}</span>
              </div>
              {r.exception_text && (
                <pre className="mt-0.5 ml-32 whitespace-pre-wrap break-all font-mono text-[10px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-1.5 py-1" data-testid={`fs-ar-log-raw-${r.id}`}>{r.exception_text}</pre>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
